<!--
  @component LogStream — live event-stream viewer.
  tier:      primitive (visual atom + interaction chrome)
  purpose:   shared shell for any "ordered event feed" panel — the
             Logs screen, per-screen embedded mini-timelines, the
             admin Operations panel's tail view. Owns the chrome
             (filter input + arc selector + live-tail toggle + row
             drill-down expand/collapse + auto-scroll). The events
             are the parent's responsibility (ProvenanceIO,
             LiveTimelineEdge, or a screen-specific projection).

  WHY A PRIMITIVE (NOT A SCREEN)
  ─────────────────────────────────────────────────────────────────
  Same shape gets used in:
    - LogsScreen (C4)         — full-page log feed
    - admin OperationsPanel (later)  — embedded tail
    - ProjectOverview          (later) — inline "recent events"

  Parent supplies `events`; LogStream renders + filters + drills.
  Parent decides what an "event" is — the row contract is the
  loose DeploymentStreamEvent-ish shape {atMs, origin, op, code,
  detail?}. Anything that satisfies that interface plugs in.
-->
<script lang="ts" module>
  /** The row shape LogStream renders. Intentionally structural so
   *  any event source (ProvenanceIO, LiveTimelineEdge, a screen-
   *  scoped projection, a faked test feed) plugs in. */
  export interface LogStreamEvent {
    readonly atMs: number;
    readonly origin: string;
    readonly op: string;
    readonly code: string;
    readonly detail?: unknown;
  }
</script>

<script lang="ts">
  import StatusPill from "./StatusPill.svelte";

  interface Props {
    /** Events to render. Newest last (the canonical ordered append-
     *  only ledger from ProvenanceIO). LogStream reverses for
     *  display so newest renders at top. */
    events: readonly LogStreamEvent[];
    /** Whether the live-tail toggle is on. Parent owns it so the
     *  toggle can also gate any underlying subscription. */
    liveTail?: boolean;
    /** Called when the user toggles live-tail. */
    onLiveTailChange?: (next: boolean) => void;
    /** Optional empty-state copy. */
    emptyLabel?: string;
    /** Maximum rows to render at once (newest N). Older rows live
     *  on; this is just a display cap to keep DOM cheap. */
    maxRows?: number;
    /** Arc options shown in the arc-prefix selector. Default = the
     *  8 deployment arcs. Empty disables the selector. */
    arcOptions?: readonly { label: string; value: string }[];
  }
  const {
    events,
    liveTail = false,
    onLiveTailChange,
    emptyLabel = "no events yet",
    maxRows = 500,
    arcOptions = [
      { label: "all",       value: "" },
      { label: "release.",  value: "release." },
      { label: "traffic.",  value: "traffic." },
      { label: "db.",       value: "db." },
      { label: "storage.",  value: "storage." },
      { label: "cache.",    value: "cache." },
      { label: "queue.",    value: "queue." },
      { label: "telemetry.",value: "telemetry." },
      { label: "rollback.", value: "rollback." },
      { label: "ephemeral.",value: "ephemeral." },
    ],
  }: Props = $props();

  // ── Local filter state ──────────────────────────────────────────
  let arcPrefix = $state("");
  let query = $state("");
  let severity = $state<"all" | "ok" | "warn" | "error" | "info">("all");

  // ── Filter pipeline ─────────────────────────────────────────────
  function classify(o: string): "ok" | "warn" | "error" | "info" {
    if (o === "ok") return "ok";
    if (o === "runtime" || o === "circuit") return "error";
    if (o === "domain") return "warn";
    return "info";
  }

  const filtered = $derived.by(() => {
    const q = query.trim().toLowerCase();
    const out: LogStreamEvent[] = [];
    for (let i = events.length - 1; i >= 0 && out.length < maxRows; i -= 1) {
      const e = events[i];
      if (!e) continue;
      if (arcPrefix && !e.op.startsWith(arcPrefix)) continue;
      if (severity !== "all" && classify(e.origin) !== severity) continue;
      if (q) {
        const hay = `${e.op} ${e.code} ${e.origin}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      out.push(e);
    }
    return out;
  });

  // ── Row drill-down ──────────────────────────────────────────────
  let expandedKey = $state<string | null>(null);
  function keyOf(e: LogStreamEvent): string {
    return `${e.atMs}:${e.op}:${e.code}`;
  }
  function toggle(e: LogStreamEvent): void {
    const k = keyOf(e);
    expandedKey = expandedKey === k ? null : k;
  }
  function fmtDetail(d: unknown): string {
    if (d === null || d === undefined) return "—";
    if (typeof d === "string" || typeof d === "number" || typeof d === "boolean")
      return String(d);
    try { return JSON.stringify(d, null, 2); }
    catch { return "[unserialisable]"; }
  }

  function fmtTime(ms: number): string {
    const d = new Date(ms);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    const ms3 = String(d.getMilliseconds()).padStart(3, "0");
    return `${hh}:${mm}:${ss}.${ms3}`;
  }

  function clearFilters(): void {
    arcPrefix = "";
    query = "";
    severity = "all";
  }

  const filtersActive = $derived(
    arcPrefix !== "" || query.trim() !== "" || severity !== "all",
  );
</script>

<div class="logstream">
  <!-- ── Toolbar ─────────────────────────────────────────────── -->
  <div class="toolbar">
    <input
      class="search"
      type="search"
      placeholder="filter by op, code, origin…"
      bind:value={query}
    />
    {#if arcOptions.length > 0}
      <select bind:value={arcPrefix} aria-label="Arc filter">
        {#each arcOptions as opt (opt.value)}
          <option value={opt.value}>{opt.label}</option>
        {/each}
      </select>
    {/if}
    <select bind:value={severity} aria-label="Severity filter">
      <option value="all">all severities</option>
      <option value="ok">ok</option>
      <option value="warn">warn</option>
      <option value="error">error</option>
      <option value="info">info</option>
    </select>
    {#if filtersActive}
      <button type="button" class="clear" onclick={clearFilters}>clear</button>
    {/if}

    <div class="spacer"></div>

    <label class="tail">
      <input
        type="checkbox"
        checked={liveTail}
        onchange={(e) => onLiveTailChange?.((e.currentTarget as HTMLInputElement).checked)}
      />
      <span>live tail{liveTail ? " ●" : ""}</span>
    </label>
  </div>

  <!-- ── Row count meta ──────────────────────────────────────── -->
  <p class="meta">
    {filtered.length} of {events.length} event{events.length === 1 ? "" : "s"}
    {#if filtered.length === maxRows}
      <span class="cap">· capped at {maxRows}</span>
    {/if}
  </p>

  <!-- ── Stream ──────────────────────────────────────────────── -->
  <div class="stream" data-tail={liveTail}>
    {#if filtered.length === 0}
      <p class="empty">{filtersActive ? "no events match the filters" : emptyLabel}</p>
    {:else}
      <ol class="rows">
        {#each filtered as e (keyOf(e))}
          {@const k = keyOf(e)}
          {@const sev = classify(e.origin)}
          <!-- svelte-ignore a11y_no_noninteractive_element_to_interactive_role
               — log rows are click-to-expand; role+tabindex+key handler
               is the intended pattern for the drill-down interaction. -->
          <li
            class="row"
            class:expanded={expandedKey === k}
            data-sev={sev}
            onclick={() => toggle(e)}
            onkeydown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); toggle(e); } }}
            role="button"
            tabindex="0"
          >
            <span class="time"><code>{fmtTime(e.atMs)}</code></span>
            <StatusPill origin={e.origin} compact />
            <span class="op"><code>{e.op}</code></span>
            <span class="code"><code>{e.code}</code></span>
            {#if expandedKey === k}
              <pre class="detail">{fmtDetail(e.detail)}</pre>
            {/if}
          </li>
        {/each}
      </ol>
    {/if}
  </div>
</div>

<style>
  .logstream {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    font-family: var(--type-font);
  }

  /* Toolbar */
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
  }
  .toolbar input,
  .toolbar select {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: 0.5px solid var(--border-input);
    background: var(--surface-sunken);
    color: var(--text-primary);
    font: inherit;
    font-size: 12px;
  }
  .toolbar input.search {
    flex: 1 1 240px;
    font-family: var(--type-mono);
  }
  .toolbar input:focus,
  .toolbar select:focus {
    outline: none;
    border-color: var(--border-focus);
  }
  .clear {
    appearance: none;
    background: transparent;
    border: 0.5px solid var(--border-subtle);
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
    padding: var(--space-1, 4px) var(--space-2);
    font: inherit;
    font-size: 11px;
    cursor: pointer;
  }
  .clear:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  .spacer { flex: 1; }
  .tail {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    cursor: pointer;
  }
  .tail input { cursor: pointer; }

  /* Meta */
  .meta {
    margin: 0;
    font-size: 11px;
    color: var(--text-tertiary);
    font-family: var(--type-mono);
  }
  .cap { color: var(--warning); }

  /* Stream */
  .stream {
    background: var(--surface-sunken);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    padding: var(--space-2);
    max-height: 60vh;
    overflow: auto;
    font-family: var(--type-mono);
  }
  .stream[data-tail="true"] {
    border-color: var(--accent-border);
    box-shadow: 0 0 0 1px var(--accent-glow);
  }
  .empty {
    margin: 0;
    padding: var(--space-5) var(--space-4);
    font-family: var(--type-font);
    font-size: 12px;
    color: var(--text-tertiary);
    text-align: center;
    font-style: italic;
  }

  /* Rows */
  .rows {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }
  .row {
    display: grid;
    grid-template-columns: 100px auto 1fr 1fr;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border-bottom: 0.5px solid var(--border-subtle);
    cursor: pointer;
    transition: background var(--ease-fast);
    font-size: 11px;
  }
  .row:hover { background: var(--bg-hover); }
  .row:focus-visible {
    outline: none;
    background: var(--bg-hover);
    box-shadow: inset 0 0 0 1px var(--border-focus);
  }
  .row.expanded {
    background: var(--bg-selected);
    grid-template-columns: 1fr;
  }
  .row.expanded > :not(.detail) {
    display: none;
  }
  .row[data-sev="error"] { border-left: 2px solid var(--error); padding-left: calc(var(--space-3) - 2px); }
  .row[data-sev="warn"]  { border-left: 2px solid var(--warning); padding-left: calc(var(--space-3) - 2px); }
  .row[data-sev="ok"]    { border-left: 2px solid var(--success); padding-left: calc(var(--space-3) - 2px); }

  .time code,
  .op code,
  .code code {
    font-family: var(--type-mono);
    font-size: 11px;
  }
  .time code   { color: var(--text-tertiary); }
  .op code     { color: var(--text-primary); }
  .code code   { color: var(--text-secondary); }

  /* Expanded detail */
  .detail {
    margin: 0;
    padding: var(--space-3);
    background: var(--surface-base);
    border-radius: var(--radius-sm);
    color: var(--text-secondary);
    font-size: 11px;
    line-height: 1.5;
    max-height: 240px;
    overflow: auto;
    white-space: pre-wrap;
  }
</style>
